import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
    Envelope,
    Ticket,
    CurrencyInr,
    TrendUp,
    CheckCircle,
    XCircle,
    Clock,
} from "@phosphor-icons/react";

const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString("en-IN", {
            day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
        });
    } catch { return "—"; }
};

const fmtINR = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

const CAMPAIGN_LABEL = {
    renewal_3d_before: "3 days before expiry",
    renewal_1d_after: "1 day after expiry",
};

export default function AdminRenewalEmails() {
    const [summary, setSummary] = useState(null);
    const [sends, setSends] = useState([]);
    const [campaignFilter, setCampaignFilter] = useState("");
    const [loading, setLoading] = useState(false);

    const loadSummary = () =>
        api.get("/admin/renewal-emails/summary")
            .then((r) => setSummary(r.data))
            .catch(() => toast.error("Failed to load summary"));

    const loadSends = async () => {
        setLoading(true);
        try {
            const params = { limit: 100 };
            if (campaignFilter) params.campaign = campaignFilter;
            const r = await api.get("/admin/renewal-emails/sends", { params });
            setSends(r.data.items || []);
        } catch {
            toast.error("Failed to load sends");
        } finally { setLoading(false); }
    };

    useEffect(() => { loadSummary(); }, []);
    useEffect(() => { loadSends(); }, [campaignFilter]);  // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8" data-testid="admin-renewal-emails-page">
            <div className="mb-6">
                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#A3A3A3] mb-1">
                    Retention
                </p>
                <h1 className="font-heading text-2xl sm:text-4xl font-black tracking-tight">
                    Renewal emails
                </h1>
                <p className="text-sm text-[#525252] mt-1">
                    Automated Pro-renewal drip — sends, code redemptions, and attributed revenue.
                </p>
            </div>

            {summary && (
                <>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="renewal-kpis">
                        {[
                            { label: "Total sends", value: summary.total_sends, icon: Envelope, tone: "text-ink" },
                            { label: "Codes issued", value: summary.codes_issued, icon: Ticket, tone: "text-cobalt" },
                            { label: "Redemption rate", value: `${summary.redemption_rate_pct}%`, icon: TrendUp, tone: summary.redemption_rate_pct >= 15 ? "text-emerald-600" : summary.redemption_rate_pct >= 5 ? "text-splash" : "text-[#525252]" },
                            { label: "Attributed revenue", value: fmtINR(summary.attributed_revenue_inr), icon: CurrencyInr, tone: "text-emerald-600" },
                        ].map(({ label, value, icon: Icon, tone }) => (
                            <div key={label} className="bg-white border border-[#E5E5E5] p-5">
                                <p className="text-[10px] font-black uppercase tracking-wider text-[#A3A3A3] mb-2 flex items-center gap-1.5">
                                    <Icon size={12} weight="bold" /> {label}
                                </p>
                                <p className={`font-heading text-3xl font-black tracking-tighter ${tone}`}>{value}</p>
                            </div>
                        ))}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
                        {["renewal_3d_before", "renewal_1d_after"].map((key) => {
                            const n = summary.sends_by_campaign?.[key] || 0;
                            const n30 = summary.sends_30d?.[key] || 0;
                            const n7 = summary.sends_7d?.[key] || 0;
                            return (
                                <div key={key} className="bg-white border border-[#E5E5E5] p-4">
                                    <p className="text-[10px] font-black uppercase tracking-wider text-[#A3A3A3]">
                                        {CAMPAIGN_LABEL[key]}
                                    </p>
                                    <p className="font-heading text-2xl font-black tracking-tight mt-1">{n}</p>
                                    <p className="text-[11px] text-[#525252]">
                                        {n30} in 30d · {n7} in 7d
                                    </p>
                                </div>
                            );
                        })}
                    </div>
                </>
            )}

            {/* Filters */}
            <div className="flex items-center gap-2 mb-4 flex-wrap">
                {[
                    { key: "", label: "All" },
                    { key: "renewal_3d_before", label: "3d before" },
                    { key: "renewal_1d_after", label: "1d after" },
                ].map((c) => (
                    <button
                        key={c.key || "all"}
                        onClick={() => setCampaignFilter(c.key)}
                        data-testid={`renewal-filter-${c.key || "all"}`}
                        className={`text-[10px] font-black uppercase tracking-wider px-3 py-1.5 border transition-colors ${
                            campaignFilter === c.key ? "bg-ink text-white border-ink" : "border-[#E5E5E5] text-[#525252] hover:border-ink"
                        }`}
                    >
                        {c.label}
                    </button>
                ))}
            </div>

            {loading && <p className="text-sm text-[#A3A3A3]">Loading…</p>}
            {!loading && sends.length === 0 && (
                <div className="bg-white border border-[#E5E5E5] p-8 text-center" data-testid="renewal-empty">
                    <Envelope size={28} weight="regular" className="text-[#A3A3A3] mx-auto mb-2" />
                    <p className="text-sm text-[#525252]">
                        No renewal emails yet. The daily scheduler runs at 09:15 IST.
                    </p>
                </div>
            )}
            {sends.length > 0 && (
                <>
                {/* Mobile cards */}
                <div className="sm:hidden space-y-3">
                    {sends.map((s) => (
                        <div key={`m-${s.user_id}-${s.code}`} data-testid={`renewal-send-card-${s.code}`} className="bg-white border border-[#E5E5E5] p-4">
                            <div className="flex items-start justify-between gap-2 mb-2">
                                <div className="min-w-0">
                                    <p className="font-heading font-bold text-ink truncate">{s.user_name}</p>
                                    <p className="text-[10px] font-mono text-[#A3A3A3] truncate">{s.email_masked}</p>
                                </div>
                                {s.redeemed ? (
                                    <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider text-emerald-700 bg-emerald-50 px-2 py-0.5 shrink-0">
                                        <CheckCircle size={10} weight="fill" /> Redeemed
                                    </span>
                                ) : s.code_expired ? (
                                    <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider text-[#A3A3A3] bg-[#F5F5F5] px-2 py-0.5 shrink-0">
                                        <XCircle size={10} weight="fill" /> Expired
                                    </span>
                                ) : s.channel === "suppressed" ? (
                                    <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider text-red-700 bg-red-50 px-2 py-0.5 shrink-0">
                                        <XCircle size={10} weight="fill" /> Suppressed
                                    </span>
                                ) : (
                                    <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider text-cobalt bg-cobalt/10 px-2 py-0.5 shrink-0">
                                        <Clock size={10} weight="bold" /> Pending
                                    </span>
                                )}
                            </div>
                            <div className="pt-2 border-t border-[#F5F5F5] grid grid-cols-2 gap-2 text-xs">
                                <div>
                                    <p className="text-[9px] uppercase tracking-wider text-[#A3A3A3]">Campaign</p>
                                    <p>{CAMPAIGN_LABEL[s.campaign] || s.campaign}</p>
                                </div>
                                <div>
                                    <p className="text-[9px] uppercase tracking-wider text-[#A3A3A3]">Code</p>
                                    <p className="font-mono truncate">{s.code}</p>
                                </div>
                                <div>
                                    <p className="text-[9px] uppercase tracking-wider text-[#A3A3A3]">Sent</p>
                                    <p className="text-[#525252]">{fmtDate(s.sent_at)}</p>
                                </div>
                                <div>
                                    <p className="text-[9px] uppercase tracking-wider text-[#A3A3A3]">Pro expiry</p>
                                    <p className="text-[#525252]">{fmtDate(s.pro_expiry)}</p>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
                {/* Desktop table */}
                <div className="hidden sm:block bg-white border border-[#E5E5E5] overflow-x-auto">
                    <table className="w-full text-sm min-w-[900px]">
                        <thead className="bg-[#FAFAFA] border-b border-[#E5E5E5]">
                            <tr className="text-left">
                                {["User", "Contact", "Campaign", "Code", "Sent", "Pro expiry", "Status"].map((h) => (
                                    <th key={h} className="px-4 py-3 text-[10px] font-black uppercase tracking-wider text-[#525252]">{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {sends.map((s) => (
                                <tr key={`${s.user_id}-${s.code}`} data-testid={`renewal-send-${s.code}`} className="border-b border-[#F5F5F5]">
                                    <td className="px-4 py-3">
                                        <p className="font-heading font-bold text-ink">{s.user_name}</p>
                                    </td>
                                    <td className="px-4 py-3 text-xs font-mono">{s.email_masked}</td>
                                    <td className="px-4 py-3 text-xs">
                                        {CAMPAIGN_LABEL[s.campaign] || s.campaign}
                                    </td>
                                    <td className="px-4 py-3 text-xs font-mono">{s.code}</td>
                                    <td className="px-4 py-3 text-xs text-[#525252]">{fmtDate(s.sent_at)}</td>
                                    <td className="px-4 py-3 text-xs text-[#525252]">{fmtDate(s.pro_expiry)}</td>
                                    <td className="px-4 py-3">
                                        {s.redeemed ? (
                                            <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider text-emerald-700 bg-emerald-50 px-2 py-0.5">
                                                <CheckCircle size={10} weight="fill" /> Redeemed
                                            </span>
                                        ) : s.code_expired ? (
                                            <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider text-[#A3A3A3] bg-[#F5F5F5] px-2 py-0.5">
                                                <XCircle size={10} weight="fill" /> Expired
                                            </span>
                                        ) : s.channel === "suppressed" ? (
                                            <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider text-red-700 bg-red-50 px-2 py-0.5">
                                                <XCircle size={10} weight="fill" /> Suppressed
                                            </span>
                                        ) : (
                                            <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider text-cobalt bg-cobalt/10 px-2 py-0.5">
                                                <Clock size={10} weight="bold" /> Pending
                                            </span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                </>
            )}
        </div>
    );
}
